/**
 * The owner's home: the AI Studio start page (default) or the classic overview.
 * Stored per user on the server (GET/PUT /api/agent-studio/me/home-view) and mirrored
 * in localStorage, so the first render already knows where "AI Studio" leads.
 */
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_BASE_URL;
const KEY = 'agentStudio.homeView';
const EVENT = 'ub:home-view';

export const STUDIO_PATH = '/agent-studio/skills';
export const CLASSIC_PATH = '/overview';
// switches an open studio between Build and Test (detail: 'build' | 'test'), e.g. from the header's "Test agent"
export const MODE_EVENT = 'agentStudio:mode';

function readCached() {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'classic' || v === 'studio' ? v : null;
  } catch (_) {
    return null;
  }
}

function remember(view) {
  try { localStorage.setItem(KEY, view); } catch (_) { /* storage unavailable */ }
}

/** Switch the home view now (all mounted users of the hook follow) and store it on the server. */
export async function saveHomeView(view) {
  remember(view);
  window.dispatchEvent(new CustomEvent(EVENT, { detail: view }));
  try {
    await fetch(`${API}agent-studio/me/home-view`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ view }),
    });
  } catch (_) { /* the local choice still applies */ }
}

function readToken() {
  try { return localStorage.getItem('token') || ''; } catch (_) { return ''; }
}

/** { view: 'studio' | 'classic', ready } — ready once the server answered (or a cached value exists).
 *  Asks only with a session and again when the session changes: the app shell mounts before a sign-in
 *  (login page, the Wix entry's /auth/wix/resolve), and an old token's 401 would send that sign-in to the
 *  login page (Robert, 19.10.2026). */
export function useHomeView() {
  const cached = readCached();
  const token = readToken();
  const [view, setView] = useState(cached || 'studio');
  const [ready, setReady] = useState(cached !== null);

  useEffect(() => {
    const follow = (e) => setView(e.detail === 'classic' ? 'classic' : 'studio');
    window.addEventListener(EVENT, follow);
    return () => window.removeEventListener(EVENT, follow);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    fetch(`${API}agent-studio/me/home-view`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return;
        if (d && (d.view === 'classic' || d.view === 'studio')) {
          remember(d.view);
          setView(d.view);
        }
        setReady(true);
      })
      .catch(() => alive && setReady(true));
    return () => { alive = false; };
  }, [token]);

  return { view, ready };
}
